import { useState, useCallback } from 'react';
import { API } from '@/utils/API';

interface UseUpdateSubStatusResult {
  updateSubStatus: (
    submissionId: string,
    subStatus: string,
    callerName: string,
    extraHeaders?: Record<string, string>
  ) => Promise<void>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateSubStatus = (): UseUpdateSubStatusResult => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateSubStatus = useCallback(
    async (
      submissionId: string,
      subStatus: string,
      callerName: string,
      extraHeaders?: Record<string, string>
    ) => {
      setIsUpdating(true);
      setError(null);

      try {
        const response = await API.authenticated.fetch(
          `/submissions/${submissionId}/status`,
          {
            method: 'PUT',
            headers: extraHeaders,
            body: JSON.stringify({
              status: {
                sub_status: subStatus,
                caller_name: callerName,
              },
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(
            errorData.error || 'Failed to update submission sub-status'
          );
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Failed to update submission sub-status';
        setError(errorMessage);
        throw err;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  return {
    updateSubStatus,
    isUpdating,
    error,
  };
};
